import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGenerateMeetingNote } from '../hooks/useGenerateMeetingNote';
import { MeetingNoteStepProps } from '../types';

type Props = MeetingNoteStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function NoteProcessingStep({ data, onSubmittingChange }: Props) {
  const { generateMeetingNote } = useGenerateMeetingNote();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submit = async () => {
    if (!data.client?.id) {
      notifyAndLogError(
        'No client selected',
        'Please select a client and try again.',
        new Error('Missing client ID'),
      );
      return;
    }
    if (!data.selectedRecordingId) {
      notifyAndLogError(
        'No source selected',
        'Please select a source and try again.',
        new Error('Missing recording ID'),
      );
      return;
    }
    if (!data.selectedTemplateId && !data.selectedAgendaFileId) {
      notifyAndLogError(
        'No template or agenda selected',
        'Please select a template or agenda and try again.',
        new Error('Missing template/agenda ID'),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await generateMeetingNote(data.client.id, {
        dataUploadFileId: data.selectedRecordingId,
        templateId: data.selectedTemplateId,
        agendaFileId: data.selectedAgendaFileId,
        summarise: data.summarise,
      });
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again.', error, {
        clientId: data.client.id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!data.client?.id || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submit();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
